/**
 * Place to store global mutable state pertaining to TurnTo.
 * The `state` property within the store class contains the
 * actual data that changes, access should be done through the
 * getters with mutation done through the setters.
 *
 * @see https://v3.vuejs.org/guide/state-management.html#simple-state-management-from-scratch
 */

import { reactive, App } from 'vue'
import logger from 'util/logger'

export interface Loader {
  type: string
  getInstance?: () => App
  getInstances?: () => Record<string, App>
  getWidgetDivIds?: () => Array<string>
  initialize?: (divId?: string) => void
  initializeAll?: () => void
}

/**
 * Interface for the state property inside the store
 */
export interface IStoreState {
  catItemId?: number
  disableAllCookies?: boolean
  disableTargetingCookies?: boolean
  parentCategoryIds: Array<number>
  brand: string
  loaders: Array<Loader>
  sku?: string
  user?: IUserState
  vpcId?: number
}

export interface IUserState {
  id?: number
  isModerator?: boolean
  firstName?: string
  lastName?: string
  nickName?: string
}

/**
 * The store class itself. An instance is created below and exported as
 * the default export.
 */
export class Store {
  state: IStoreState = reactive({
    catItemId: null,
    disableAllCookies: false,
    disableTargetingCookies: false,
    parentCategoryIds: null,
    brand: null,
    vpcId: null,
    loaders: [],
    sku: '',
    user: {
      id: null,
      isModerator: false,
      firstName: '',
      lastName: '',
      nickName: ''
    }
  })

  debug = true

  set catItemId(catItemId: number) {
    logger.debug(`globalStore.state.catItemId set with value "${catItemId}"`)
    this.state.catItemId = catItemId
  }

  get catItemId(): number {
    return this.state.catItemId
  }

  get disableAllCookies(): boolean {
    return this.state.disableAllCookies
  }

  set disableAllCookies(disableAllCookies: boolean) {
    this.state.disableAllCookies = disableAllCookies
  }

  get disableTargetingCookies(): boolean {
    return this.state.disableTargetingCookies
  }

  set disableTargetingCookies(disableTargetingCookies: boolean) {
    this.state.disableTargetingCookies = disableTargetingCookies
  }

  set parentCategoryIds(parentCategoryIds: Array<number>) {
    logger.debug(`globalStore.state.parentCategoryIds set with value "${parentCategoryIds}"`)
    this.state.parentCategoryIds = parentCategoryIds
  }

  get parentCategoryIds(): Array<number> {
    return this.state.parentCategoryIds
  }

  set brand(brand: string) {
    logger.debug(`globalStore.state.brand set with value "${brand}"`)
    this.state.brand = brand
  }

  get brand(): string {
    return this.state.brand
  }

  get loaders(): Array<Loader> {
    return this.state.loaders
  }

  set loaders(loaders: Array<Loader>) {
    this.state.loaders = loaders
  }

  set sku(sku: string) {
    logger.debug(`globalStore.state.sku set with value "${sku}"`)
    if (typeof sku === 'number') {
      this.state.sku = (sku as number).toString()
      return
    }
    this.state.sku = sku
  }

  get sku(): string {
    return this.state.sku
  }

  get user(): IUserState {
    return this.state.user
  }

  // NOTE: this should only be called from token-service
  set user(user: IUserState) {
    if (!user) {
      this.state.user = {
        id: null,
        isModerator: false,
        firstName: '',
        lastName: '',
        nickName: ''
      } as IUserState
      return
    }
    this.state.user = user
  }

  set vpcId(vpcId: number) {
    logger.debug(`globalStore.state.vpcId set with value "${vpcId}"`)
    this.state.vpcId = vpcId
  }

  get vpcId(): number {
    return this.state.vpcId
  }
}

export default new Store()
